import { useState } from 'react';
import Reveal from '@/components/base/Reveal';
import { useToast } from '@/components/feature/ToastProvider';
import { communityTalks } from '@/mocks/homeData';

const avatarTone: Record<string, string> = {
  primary: 'bg-primary-100 text-primary-800',
  secondary: 'bg-secondary-100 text-secondary-800',
  accent: 'bg-accent-100 text-accent-900',
};

const topics = ['#古装新剧花絮', '#综艺名场面', '#联名设计稿', '#音乐节加场', '#短剧新黑马'];

export default function CommunityTalk() {
  const [draft, setDraft] = useState('');
  const { showToast } = useToast();

  const handlePost = () => {
    if (!draft.trim()) {
      showToast('演示环境：先写点什么再发布吧');
      return;
    }
    showToast('演示环境：讨论内容暂不提交');
  };

  return (
    <section id="community" className="border-y border-background-200/80 bg-background-100">
      <div className="mx-auto w-full max-w-shell px-4 py-20 md:px-6 md:py-28">
        <Reveal>
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800">
              <i className="ri-chat-smile-2-line" />
              社区热议
            </span>
            <h2 className="mt-5 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
              不是一个人在刷，讨论区里总有人补上你漏看的那一段
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              有人负责整理节点，有人负责提出问题。你在讨论区留下的观察，会帮到下一个刚点进来看的人。
            </p>
          </div>
        </Reveal>

        <Reveal delay={80}>
          <div className="mt-8 flex flex-wrap gap-2.5">
            {topics.map((topic) => (
              <a
                key={topic}
                href="#community"
                className="whitespace-nowrap rounded-full border border-background-300/80 bg-background-50 px-4 py-2 text-xs text-foreground-700 transition-colors hover:border-secondary-400 hover:text-secondary-800 md:text-sm"
              >
                {topic}
              </a>
            ))}
          </div>
        </Reveal>

        <div className="mt-10 grid grid-cols-1 gap-5 lg:grid-cols-3">
          {communityTalks.map((talk, index) => (
            <Reveal key={talk.id} delay={index * 90}>
              <article className="flex h-full flex-col rounded-lg border border-background-200/80 bg-background-50 p-5">
                <div className="flex items-center gap-3">
                  <span
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-heading text-sm font-bold ${avatarTone[talk.avatarTone]}`}
                  >
                    {talk.name.slice(0, 1)}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-foreground-950">{talk.name}</p>
                    <p className="flex items-center gap-2 text-xs text-foreground-500">
                      <span className="rounded-full bg-background-200/80 px-2 py-0.5">{talk.badge}</span>
                      <span>{talk.time}</span>
                    </p>
                  </div>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-foreground-700">{talk.content}</p>
                <div className="mt-auto flex items-center justify-between pt-5 text-xs text-foreground-500">
                  <span className="text-secondary-700">{talk.topic}</span>
                  <span className="flex items-center gap-4">
                    <span className="flex items-center gap-1.5">
                      <i className="ri-thumb-up-line text-sm" />
                      {talk.likes}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <i className="ri-chat-3-line text-sm" />
                      {talk.replies}
                    </span>
                  </span>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={120}>
          <div className="mt-10 rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-6">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-foreground-950 font-heading text-sm font-bold text-background-50">
                我
              </span>
              <p className="text-sm text-foreground-600">
                说点具体的吧，比如「哪一步我没看懂」或者「我补充一个能对上的细节」。
              </p>
            </div>
            <div className="mt-4 rounded-md border border-background-300/80 bg-background-100 p-3">
              <textarea
                value={draft}
                maxLength={200}
                onChange={(event) => setDraft(event.target.value)}
                placeholder="写下你的观察或疑问……"
                className="h-20 w-full resize-none bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              <div className="flex items-center justify-between border-t border-background-200/80 pt-3">
                <span className="text-xs text-foreground-400">{draft.length} / 200</span>
                <button
                  type="button"
                  onClick={handlePost}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-secondary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-secondary-700"
                >
                  <i className="ri-send-plane-line text-base" />
                  发布讨论
                </button>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}